import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import DashboardLayout from './pages/dashboard';
import DashboardProtectedRoute from './components/DashboardProtectedRoute';
import LeadsList from './pages/dashboard/leads/LeadsList';
import LeadDetailRedirect from './pages/dashboard/leads/LeadDetailRedirect';
import CampaignsList from './pages/dashboard/campaigns/CampaignsList';
import CampaignDetail from './pages/dashboard/campaigns/CampaignDetail';
import ContactsList from './pages/dashboard/contacts/ContactsList';
import ContactDetail from './pages/dashboard/contacts/ContactDetail';
import RolesList from './pages/dashboard/roles/RolesList';
import RoleForm from './pages/dashboard/roles/RoleForm';
import RoleDetail from './pages/dashboard/roles/RoleDetail';
import PermissionsPage from './pages/dashboard/permissions/PermissionsPage';
import CollaboratorsList from './pages/dashboard/collaborators/CollaboratorsList';
import CollaboratorForm from './pages/dashboard/collaborators/CollaboratorForm';
import CollaboratorDetail from './pages/dashboard/collaborators/CollaboratorDetail';
import DepartmentsList from './pages/dashboard/departments/DepartmentsList';
import DepartmentForm from './pages/dashboard/departments/DepartmentForm';
import DepartmentDetail from './pages/dashboard/departments/DepartmentDetail';
import LeadOriginsList from './pages/dashboard/leadOrigins/LeadOriginsList';
import LeadOriginForm from './pages/dashboard/leadOrigins/LeadOriginForm';
import LeadOriginDetail from './pages/dashboard/leadOrigins/LeadOriginDetail';
import CustomersList from './pages/dashboard/customers/CustomersList';
import CustomerForm from './pages/dashboard/customers/CustomerForm';
import CustomerDetail from './pages/dashboard/customers/CustomerDetail';
import BusinessesList from './pages/dashboard/businesses/BusinessesList';
import BusinessForm from './pages/dashboard/businesses/BusinessForm';
import BusinessDetail from './pages/dashboard/businesses/BusinessDetail';
import IntegrationsList from './pages/dashboard/integrations/IntegrationsList';
import IntegrationDetail from './pages/dashboard/integrations/IntegrationDetail';
import IntegrationForm from './pages/dashboard/integrations/IntegrationForm';
import OrganizationChart from './pages/dashboard/organization/OrganizationChart';
import LoadingState from './components/dashboard/LoadingState';

// Chunks próprios: os gráficos (recharts) só baixam quando uma página de métricas abre.
const MetricsPage = lazy(() => import('./pages/dashboard/metrics/MetricsPage'));
const AiMetricsPage = lazy(() => import('./pages/dashboard/metrics/AiMetricsPage'));
const JourneyPage = lazy(() => import('./pages/dashboard/metrics/JourneyPage'));
const ServiceBoardPage = lazy(() => import('./pages/dashboard/leads/board/ServiceBoardPage'));

function TenantRoute() {
  const { isInternalOnly } = useAuth();
  return isInternalOnly ? <Navigate to="/customers" replace /> : <Outlet />;
}

export default function DashboardRoutes() {
  const { isInternalOnly } = useAuth();
  const home = isInternalOnly ? '/customers' : '/leads';

  return (
    <Routes>
      <Route
        element={
          <DashboardProtectedRoute>
            <DashboardLayout />
          </DashboardProtectedRoute>
        }
      >
        {/* Tenant-scoped pages: need a customer, so an internal user
            without one is sent to customer management instead. */}
        <Route element={<TenantRoute />}>
          <Route path="/leads" element={<LeadsList />} />
          <Route path="/leads/board" element={<Suspense fallback={<LoadingState />}><ServiceBoardPage /></Suspense>} />
          <Route path="/leads/:uuid" element={<LeadDetailRedirect />} />
          <Route path="/campaigns" element={<CampaignsList />} />
          <Route path="/campaigns/:id" element={<CampaignDetail />} />
          <Route path="/contacts" element={<ContactsList />} />
          <Route path="/contacts/:id" element={<ContactDetail />} />
          <Route path="/roles" element={<RolesList />} />
          <Route path="/roles/new" element={<RoleForm />} />
          <Route path="/roles/:id/edit" element={<RoleForm />} />
          <Route path="/roles/:id" element={<RoleDetail />} />
          <Route path="/permissions" element={<PermissionsPage />} />
          <Route path="/collaborators" element={<CollaboratorsList />} />
          <Route path="/collaborators/new" element={<CollaboratorForm />} />
          <Route path="/collaborators/:id/edit" element={<CollaboratorForm />} />
          <Route path="/collaborators/:id" element={<CollaboratorDetail />} />
          <Route path="/departments" element={<DepartmentsList />} />
          <Route path="/departments/new" element={<DepartmentForm />} />
          <Route path="/departments/:id/edit" element={<DepartmentForm />} />
          <Route path="/departments/:id" element={<DepartmentDetail />} />
          <Route path="/lead-origins" element={<LeadOriginsList />} />
          <Route path="/lead-origins/new" element={<LeadOriginForm />} />
          <Route path="/lead-origins/:id/edit" element={<LeadOriginForm />} />
          <Route path="/lead-origins/:id" element={<LeadOriginDetail />} />
          <Route path="/integrations" element={<IntegrationsList />} />
          <Route path="/integrations/:type/:provider/new" element={<IntegrationForm />} />
          <Route path="/integrations/:type/:provider/edit" element={<IntegrationForm isEditMode />} />
          <Route path="/integrations/:type/:provider" element={<IntegrationDetail />} />
          <Route path="/metrics" element={<Suspense fallback={<LoadingState />}><MetricsPage /></Suspense>} />
          <Route path="/metrics/ai" element={<Suspense fallback={<LoadingState />}><AiMetricsPage /></Suspense>} />
          <Route path="/metrics/journey" element={<Suspense fallback={<LoadingState />}><JourneyPage /></Suspense>} />
        </Route>
        <Route path="/customers" element={<CustomersList />} />
        <Route path="/customers/new" element={<CustomerForm />} />
        <Route path="/customers/:id/edit" element={<CustomerForm />} />
        <Route path="/customers/:id" element={<CustomerDetail />} />
        {/* Stores: internal users manage every customer's, collaborators
            their own customer's (the API scopes it). */}
        <Route path="/businesses" element={<BusinessesList />} />
        <Route path="/businesses/new" element={<BusinessForm />} />
        <Route path="/businesses/:id/edit" element={<BusinessForm />} />
        <Route path="/businesses/:id" element={<BusinessDetail />} />
        {/* Org chart: internal users pick a customer, collaborators see
            their own (same API scoping as the stores). */}
        <Route path="/organization" element={<OrganizationChart />} />
      </Route>

      <Route path="/" element={<Navigate to={home} replace />} />
      <Route path="*" element={<Navigate to={home} replace />} />
    </Routes>
  );
}
